import {
  ApiResponse,
  ApiVersion,
  Assessment,
  AssessmentsOnPage,
  ComprehensiveAssessment,
  ComprehensiveAssessmentsOnPage,
  EventsOnPage,
  Fixture,
  Event,
  FixturesOnPage,
  GraphData,
  ICCReview,
  ICCReviewsOnPage,
  Login,
  Logout,
  MFAChallenge,
  MFAResponse,
  Org,
  OrgsOnPage,
  OverdueAssessmentsOnPage,
  Paginator,
  PDFUrl,
  Player,
  PlayerHistoryOnPage,
  PlayersOnPage,
  Position,
  SearchResult,
  Team,
  TeamPlayer,
  TeamsOnPage,
  Terms,
  TermsAccepted,
  Timezone,
  Timezones,
  TokenResponse,
  Tournament,
  TournamentsOnPage,
  User,
  UserRegistered,
  UserRole,
  UsersOnPage,
  Venue,
  VenuesOnPage,
  PlayerHistory,
  MergePlayer,
  ApiKeysOnPage,
  ApiKey,
  TeamPlayerReview,
  TeamPlayerReviewsOnPage,
  TeamPlayerReviewCreate,
  ExternalConcussion,
  ExternalConcussionsOnPage,
  ExportUnionData200Response,
  IgnoreEventOptions,
  IgnoreEventConfirmationInfo,
} from 'src/api';
import { api as axios, s3api } from 'boot/axios';
import { AxiosResponse } from 'axios';
import { displayErrorMessage } from 'src/service/AxiosUtils';
import Constants from 'src/constants';
import { PortalCreateHIA, PortalCreateSCAT6, PortalCreateClearance } from 'src/types/portal-types';

/**
 * passed in to api end points as a fairly standard set of filters on most of the major entity GET endpoints
 */
export interface QueryParameters {
  page?: number; // current page number: default: 1. 0 = "show all", which may be large so use with care
  pageSize?: number; // items per page: default defined by the api, probably 50
  team?: number | number[]; // only include records in these teams (array of int)
  org?: number; //|number[], // only include records in these orgs/unions (array of int)
  player?: number | number[]; // only include records for these players (array of int)
  venue?: number; //|number[], // only include records for these venues (array of int)
  tournament?: number | number[]; //|number[], // only include records for these tournaments (array of int)
  role?: string; //|number[], // only include users with these roles (array of string)
  event?: number; //|number[], // only include assessments for this event id
  iccreview?: number; //|number[], // only include assessments for this icc review id
  assessment?: number; //|number[], // get these assessment ids
  search?: string; // filter by things matching this string
  type?: string | string[]; // assessment type, user type, many other type fields
  show?: string; // for icc reviews
  outcome?: string; // comprehensive assessment outcome
  status?: string | string[]; // player status, assessment status
  sinceTime?: number; // only show results after this unixtime in milliseconds
  kickoffAfter?: number; // only show results after this unixtime in milliseconds: for /fixtures
  showAll?: number; // if > 0 show all, else show the default subset set of results
  showDeleted?: number; // if > 0 show deleted items, else show only normal results which hide deleted rows
  completedAt?: 'all' | 'incomplete' | 'complete'; // for GET /comprehensives
  includeBaselineDate?: boolean; //include the baseline date on players
  sortBy?: string;
  descending?: boolean;
  endsAtLt?: string;
  endsAtGte?: string;
}

// this is the same shape as most paginated api responses eg UsersOnPage, PlayersOnPage
export type PaginatedResponse<T> = {
  data?: T[];
  paginator?: Paginator;
};

export function isMFAChallenge(object: unknown): object is MFAChallenge {
  return (
    !!object && Object.prototype.hasOwnProperty.call(object, 'mfaRequired')
  );
}

function isSuccessful(response: AxiosResponse): boolean {
  return !!response.data;
}

// takes a response from a POST, PUT, PATCH and returns the record id affected
async function saveOne<T extends { id?: number }>(
  data: T,
  baseUrl: string,
): Promise<T | undefined> {
  if (data.id) {
    // if the record exists, update it with a PATCH and return the original
    const response = await axios.patch(`${baseUrl}/${data.id}`, data);
    return response ? data : undefined;
  } else {
    // if the record is new, create it with a POST and return the created object from the api response
    const response = await axios.post(baseUrl, data);
    return response ? (response.data as T) : undefined;
  }
}

async function getOne<T>(
  id: number,
  urlTemplate: string,
): Promise<T | undefined> {
  const response = await axios.get(urlTemplate.replace('{id}', String(id)));
  return response ? (response.data as T) : undefined;
}

async function getPage<T>(
  url: string,
  query: QueryParameters = {},
): Promise<T | undefined> {
  const response = await axios.get(url, { params: query });
  return response ? (response.data as T) : undefined;
}

async function deleteOne<T>(url: string): Promise<T | undefined> {
  const response = await axios.delete(url);
  return response ? (response.data as T) : undefined;
}

// Our API returns an S3 url to upload to and some fields that give us permission to do so
type S3FileUploadParameters = {
  url: string;
  fields: { [key: string]: string };
};

async function getS3FileUploadParameters(
  url: string,
): Promise<S3FileUploadParameters | undefined> {
  const response = await axios.post(url);
  return response ? (response.data as S3FileUploadParameters) : undefined;
}

async function uploadFile(
  toUrl: string,
  file: File,
): Promise<string | undefined> {
  // ask for permission to upload to s3
  const s3 = await getS3FileUploadParameters(toUrl);
  if (!s3) return undefined;

  // format the s3 upload and POST to amazon s3
  // see https://docs.aws.amazon.com/AWSJavaScriptSDK/v3/latest/modules/_aws_sdk_s3_presigned_post.html
  const formData = new FormData();
  Object.entries(s3.fields).forEach(([field, value]) => {
    formData.append(field, value); // authentication information
  });
  formData.append('file', file); // the actual file to upload
  s3api.post(s3.url, formData).catch(displayErrorMessage);
  // calculate the url where the file was uploaded to, so the caller can display the uploaded image
  return s3.url + '/' + (s3.fields['key'] ?? '');
}

const wait = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

async function waitForFileToExist(
  url: string,
  attemptsLeft: number,
  sleepMilliseconds = 2000,
): Promise<string | undefined> {
  // poll to see if the file exists
  try {
    // if the file exists, return the url
    await s3api.get(url, { headers: { Range: 'bytes=0-0' } });
    return url;
  } catch (error) {
    // if at max retries, report the error
    if (attemptsLeft <= 0) {
      console.log(error);
      return url; // return the url anyway: the caller may be able to do something with it
    }
    // otherwise try again
    await wait(sleepMilliseconds);
    return waitForFileToExist(url, attemptsLeft - 1); // recursive call
  }
}

// upload the file, and wait a few seconds for it to appear on S3
async function uploadFileAndWait(
  toUrl: string,
  file: File,
): Promise<string | undefined> {
  const s3url = await uploadFile(toUrl, file);
  return s3url ? waitForFileToExist(s3url, 2) : undefined;
}

// flutter and postgres v11 differ on what timezones should be allowed. So we ignore the following timezones returned by the api (postgres) to better support flutter
const ignoreTimezones = [
  'Africa/Asmera',
  'Africa/Timbuktu',
  'America/Argentina/ComodRivadavia',
  'America/Atka',
  'America/Buenos_Aires',
  'America/Catamarca',
  'America/Coral_Harbour',
  'America/Cordoba',
  'America/Ensenada',
  'America/Fort_Nelson',
  'America/Fort_Wayne',
  'America/Indianapolis',
  'America/Jujuy',
  'America/Knox_IN',
  'America/Louisville',
  'America/Mendoza',
  'America/Nuuk',
  'America/Porto_Acre',
  'America/Punta_Arenas',
  'America/Rosario',
  'America/Shiprock',
  'America/Virgin',
  'Antarctica/South_Pole',
  'Asia/Ashkhabad',
  'Asia/Atyrau',
  'Asia/Barnaul',
  'Asia/Calcutta',
  'Asia/Chongqing',
  'Asia/Chungking',
  'Asia/Dacca',
  'Asia/Famagusta',
  'Asia/Harbin',
  'Asia/Istanbul',
  'Asia/Kashgar',
  'Asia/Katmandu',
  'Asia/Macao',
  'Asia/Qostanay',
  'Asia/Saigon',
  'Asia/Tel_Aviv',
  'Asia/Thimbu',
  'Asia/Tomsk',
  'Asia/Ujung_Pandang',
  'Asia/Ulan_Bator',
  'Asia/Yangon',
  'Atlantic/Faeroe',
  'Atlantic/Jan_Mayen',
  'Australia/ACT',
  'Australia/Canberra',
  'Australia/LHI',
  'Australia/NSW',
  'Australia/North',
  'Australia/Queensland',
  'Australia/South',
  'Australia/Tasmania',
  'Australia/Victoria',
  'Australia/West',
  'Australia/Yancowinna',
  'Brazil/Acre',
  'Brazil/DeNoronha',
  'Brazil/East',
  'Brazil/West',
  'CET',
  'CST6CDT',
  'Canada/Saskatchewan',
  'Canada/Yukon',
  'Chile/Continental',
  'Chile/EasterIsland',
  'Cuba',
  'EET',
  'EST',
  'EST5EDT',
  'Egypt',
  'Eire',
  'Etc/GMT',
  'Etc/GMT+0',
  'Etc/GMT+1',
  'Etc/GMT+10',
  'Etc/GMT+11',
  'Etc/GMT+12',
  'Etc/GMT+2',
  'Etc/GMT+3',
  'Etc/GMT+4',
  'Etc/GMT+5',
  'Etc/GMT+6',
  'Etc/GMT+7',
  'Etc/GMT+8',
  'Etc/GMT+9',
  'Etc/GMT-0',
  'Etc/GMT-1',
  'Etc/GMT-10',
  'Etc/GMT-11',
  'Etc/GMT-12',
  'Etc/GMT-13',
  'Etc/GMT-14',
  'Etc/GMT-2',
  'Etc/GMT-3',
  'Etc/GMT-4',
  'Etc/GMT-5',
  'Etc/GMT-6',
  'Etc/GMT-7',
  'Etc/GMT-8',
  'Etc/GMT-9',
  'Etc/GMT0',
  'Etc/Greenwich',
  'Etc/UCT',
  'Etc/UTC',
  'Etc/Universal',
  'Etc/Zulu',
  'Europe/Astrakhan',
  'Europe/Belfast',
  'Europe/Kirov',
  'Europe/Nicosia',
  'Europe/Saratov',
  'Europe/Tiraspol',
  'Europe/Ulyanovsk',
  'Factory',
  'GB',
  'GB-Eire',
  'GMT+0',
  'GMT-0',
  'GMT0',
  'Greenwich',
  'HST',
  'Hongkong',
  'Iceland',
  'Iran',
  'Israel',
  'Jamaica',
  'Japan',
  'Kwajalein',
  'Libya',
  'MET',
  'MST',
  'MST7MDT',
  'Mexico/BajaNorte',
  'Mexico/BajaSur',
  'Mexico/General',
  'NZ',
  'NZ-CHAT',
  'Navajo',
  'PRC',
  'PST8PDT',
  'Pacific/Bougainville',
  'Pacific/Kanton',
  'Pacific/Ponape',
  'Pacific/Samoa',
  'Pacific/Truk',
  'Pacific/Yap',
  'Poland',
  'Portugal',
  'ROC',
  'ROK',
  'Singapore',
  'Turkey',
  'UCT',
  'US/Aleutian',
  'US/East-Indiana',
  'US/Indiana-Starke',
  'US/Michigan',
  'US/Samoa',
  'Universal',
  'W-SU',
  'WET',
  'Zulu',
  'posixrules',
];

/**
 * wraps axios and the World Rugby RHR api openapi
 * uses the types / data structures from openapi generator but does not use the actual method calls
 * because
 * - openapi-generator was behaving weirdly under test and we couldn't figure out why, so we bypassed it
 * - it had an opinionated way of adding bearer tokens and we needed it to work different
 * - seemed like a lot of code to do not much. wanted something simpler eg the factory classes were awkward
 *
 * this code relies on the vue axios component amd various interceptors installed on that for auth token management
 */
export const api = {
  isValidImage: (url: string | undefined) => (url ?? '').includes('.'),

  imageOrPlaceholder: (
    url: string | undefined | Array<string | undefined>,
  ): string => {
    if (Array.isArray(url)) {
      url = url.filter(api.isValidImage)[0];
      return url ?? Constants.placeholderImage;
    }
    return api.isValidImage(url) ? url ?? '' : Constants.placeholderImage;
  },

  // preferred team id
  locatePlayerImage: (
    player: Player | undefined,
    teamIds: Array<number> = [],
  ): string => {
    // a player image is from playerTeams.imageUrl, team default playerImageUrl or a placeholder
    if (!player || !player.playerTeams || player.playerTeams.length <= 0) {
      return api.imageOrPlaceholder(Constants.playerDefaultImage);
    }
    // see if there is an image for a preferred team
    const selected =
      player.playerTeams.filter((playerTeam) =>
        teamIds.includes(playerTeam.team?.id ?? 0),
      )[0] ?? player.playerTeams[0];
    return api.imageOrPlaceholder([
      selected.imageUrl,
      selected.team?.playerImageUrl,
      Constants.playerDefaultImage,
    ]);
  },

  isAuthUrl: function (url: string | undefined): boolean {
    // true if the url is for authentication: so we don't need a bearer token
    // and should not try to refresh the access token if it is expired when calling one of these endpoints
    return /\/auth\/\w+/.test(url ?? '');
  },

  requiresAccessToken: function (url: string | undefined): boolean {
    // special case: logout needs the access token so it knows which to destroy.
    return !api.isAuthUrl(url) || url == '/auth/logout';
  },

  requiresRefreshToken: function (url: string | undefined): boolean {
    // true if the endpoint needs the refresh token
    return url == '/auth/refresh';
  },

  version: async function (): Promise<ApiVersion | undefined> {
    return getOne<ApiVersion>(0, '/version');
  },

  graphs: async function (
    startTime: number,
    endTime: number,
  ): Promise<GraphData | undefined> {
    // unix time timestamps
    const response = await axios.get('/graphs', {
      params: { startTime, endTime },
    });
    return response ? (response.data as GraphData) : undefined;
  },

  getPdfUrl: async function (
    query: QueryParameters,
  ): Promise<PDFUrl | undefined> {
    const response = await axios.get('/pdf', { params: query });
    return response ? (response.data as PDFUrl) : undefined;
  },

  getExportUrl: async function (
    orgId: number,
  ): Promise<ExportUnionData200Response | undefined> {
    const response = await axios.post(`/orgs/${orgId}/export`);
    return response
      ? (response.data as ExportUnionData200Response)
      : undefined;
  },

  timeZones: async function (): Promise<Array<Timezone>> {
    const response = await axios.get('/timezones');
    if (!response || !response.data) return [];
    const zones = (response.data as Timezones).zones;
    return zones.filter((zone) => !ignoreTimezones.includes(zone.name));
  },

  search: async function (
    text: string,
    type: string | undefined,
  ): Promise<SearchResult | undefined> {
    const response = await axios.get('/search', {
      params: { search: text, type, page: 1, pageSize: 10 },
    });
    return response ? (response.data as SearchResult) : undefined;
  },

  getPositions: async function (): Promise<Position[]> {
    const response = await axios.get('/positions');
    return response?.data ? (response.data as Position[]) : [];
  },

  getNewestTerms: async function (): Promise<Terms | undefined> {
    const response = await axios.get('/auth/terms');
    return response ? (response.data as Terms) : undefined;
  },

  login: async function (
    email: string,
    password: string,
  ): Promise<TokenResponse | MFAChallenge | undefined> {
    const response = await axios.post('/auth/login', {
      email,
      password,
      interface: 'admin',
    } as Login);
    return response?.data;
  },

  verifyMFA: async function (
    mfaCode: number,
    token: string,
    device: string,
  ): Promise<TokenResponse | undefined> {
    const response = await axios.post('/auth/mfa', {
      token,
      mfaCode,
      device,
    } as MFAResponse);
    return response?.data;
  },

  resendMFA: async function (
    token: string,
    device: string,
  ): Promise<MFAChallenge | undefined> {
    const response = await axios.post('/auth/mfa', {
      token,
      device,
    } as MFAResponse);
    return !!response && isMFAChallenge(response.data)
      ? response.data
      : undefined;
  },

  logout: async function (
    accessToken: string,
    refreshToken: string,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.post('/auth/logout', {
      accessToken,
      refreshToken,
      interface: 'admin',
    } as Logout);
    return response ? (response.data as ApiResponse) : undefined;
  },

  preRegister: async function (
    expires: number,
    token: string,
    userId: number,
  ): Promise<User | undefined> {
    const response = await axios.get('/auth/register', {
      params: { id: userId, expires, token },
    });
    return response ? (response.data as User) : undefined;
  },

  register: async function (
    expires: number,
    token: string,
    user: User,
  ): Promise<UserRegistered | undefined> {
    const response = await axios.post('/auth/register', user, {
      params: { id: user.id, expires, token },
    });
    return response ? (response.data as UserRegistered) : undefined;
  },

  resendRegistration: async function (
    userId: number,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.patch(`/users/${userId}/resendAuth`);
    return response ? (response.data as ApiResponse) : undefined;
  },

  refreshToken: async function (): Promise<TokenResponse | undefined> {
    const response = await axios.get('/auth/refresh');
    return response ? (response.data as TokenResponse) : undefined;
  },

  forgotPassword: async function (email: string): Promise<boolean> {
    return isSuccessful(await axios.post('/auth/forgot', { email }));
  },

  resetPassword: async function (
    email: string,
    password: string,
    token: string,
  ): Promise<boolean> {
    const response = await axios.post('/auth/reset', {
      email,
      password,
      token,
    });
    return !!response.data;
  },

  getUser: async function (userId: number): Promise<User | undefined> {
    return getOne<User>(userId, '/users/{id}');
  },

  getUsers: async function (
    query: QueryParameters = {},
  ): Promise<UsersOnPage | undefined> {
    return getPage<UsersOnPage>('/users', query);
  },

  saveUser: async function (user: User): Promise<User | undefined> {
    // delete user.roles; // use addUserRole and deleteUserRole to change these
    // delete user.terms; // use acceptTerms to when user has accepted new terms and conditions
    return saveOne({ ...user, roles: undefined, terms: undefined }, '/users');
  },

  deleteUser: async function (
    userId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/users/${userId}`);
  },

  unlockUser: async function (id: number): Promise<boolean> {
    return isSuccessful(await axios.post(`/users/${id}/unlock`));
  },

  mergeUser: async function (
    intoUserId: number,
    fromUserId: number,
  ): Promise<boolean> {
    return isSuccessful(
      await axios.patch(`/users/${intoUserId}/mergeFrom/${fromUserId}`),
    );
  },

  addUserRole: async function (
    userId: number,
    role: UserRole,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.post(`/users/${userId}/roles`, {
      ...role,
      id: undefined,
    });
    return response ? (response.data as ApiResponse) : undefined;
  },

  acceptTerms: async function (
    userId: number,
    termsId: number,
  ): Promise<boolean | undefined> {
    const response = await axios.patch(`/users/${userId}/terms`, {
      termsId,
      createdAt: new Date().toISOString(),
    } as TermsAccepted);
    return !!response.data;
  },

  deleteUserRole: async function (
    userId: number,
    userRoleId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/users/${userId}/roles/${userRoleId}`);
  },

  getOrgs: async function (
    query: QueryParameters = {},
  ): Promise<OrgsOnPage | undefined> {
    return getPage<OrgsOnPage>('/orgs', query);
  },

  getOrg: async function (orgId: number): Promise<Org | undefined> {
    return getOne<Org>(orgId, '/orgs/{id}');
  },

  saveOrg: async function (org: Org): Promise<Org | undefined> {
    return saveOne(org, '/orgs');
  },

  deleteOrg: async function (orgId: number): Promise<ApiResponse | undefined> {
    return deleteOne(`/orgs/${orgId}`);
  },

  uploadOrgLogo: async function (
    orgId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFileAndWait(`/orgs/${orgId}/uploadPhoto`, file);
  },

  addOrgPlayer: async function (
    orgId: number,
    playerId: number,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.post(`/orgs/${orgId}/players/${playerId}`);
    return response ? (response.data as ApiResponse) : undefined;
  },

  deleteOrgPlayer: async function (
    orgId: number,
    playerId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/orgs/${orgId}/players/${playerId}`);
  },

  getTeams: async function (
    query: QueryParameters = {},
  ): Promise<TeamsOnPage | undefined> {
    return getPage<TeamsOnPage>('/teams', query);
  },

  getTeam: async function (teamId: number): Promise<Team | undefined> {
    return getOne<Team>(teamId, '/teams/{id}');
  },

  saveTeam: async function (team: Team): Promise<Team | undefined> {
    return saveOne(team, '/teams');
  },

  deleteTeam: async function (
    teamId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/teams/${teamId}`);
  },

  uploadTeamLogo: async function (
    teamId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFileAndWait(`/teams/${teamId}/uploadLogo`, file);
  },

  uploadTeamDefaultPlayerImage: async function (
    teamId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFileAndWait(`/teams/${teamId}/uploadPlayerImage`, file);
  },

  uploadTeamPlayerImage: async function (
    teamId: number,
    playerId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFileAndWait(
      `/teams/${teamId}/player/${playerId}/uploadImage`,
      file,
    );
  },

  updateTeamPlayer: async function (
    teamId: number,
    playerId: number,
    details: TeamPlayer,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.patch(
      `/teams/${teamId}/players/${playerId}`,
      details,
    );
    return response ? (response.data as ApiResponse) : undefined;
  },

  getTeamPlayers: async function (
    teamId: number,
  ): Promise<TeamPlayer[] | undefined> {
    const response = await axios.get(`/teams/${teamId}/players`);
    return response ? (response.data as TeamPlayer[]) : undefined;
  },

  addTeamPlayer: async function (
    teamId: number,
    playerId: number,
    details: TeamPlayer,
  ): Promise<TeamPlayer[] | undefined> {
    const response = await axios.post(
      `/teams/${teamId}/players/${playerId}`,
      details,
    );
    return response ? (response.data as TeamPlayer[]) : undefined;
  },

  deleteTeamPlayer: async function (
    teamId: number,
    playerId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/teams/${teamId}/players/${playerId}`);
  },

  getPlayers: async function (
    query: QueryParameters = {},
  ): Promise<PlayersOnPage | undefined> {
    return getPage<PlayersOnPage>('/players', query);
  },

  getPlayer: async function (playerId: number): Promise<Player | undefined> {
    return getOne<Player>(playerId, '/players/{id}');
  },

  updatePlayer: async function (
    playerId: number,
    player: Player,
  ): Promise<Player | undefined> {
    const response = await axios.patch(`/players/${playerId}`, player);
    return response ? (response.data as Player) : undefined;
  },

  getPlayerStatus: async function (
    playerId: number,
    query: QueryParameters = {},
  ): Promise<PlayerHistoryOnPage | undefined> {
    return getPage<PlayerHistoryOnPage>(`/players/${playerId}/status`, query);
  },

  savePlayerStatus: async function (
    playerId: number,
    status: PlayerHistory,
  ): Promise<PlayerHistory | undefined> {
    return saveOne(status, `/players/${playerId}/status`);
  },

  deletePlayerStatus: async function (
    playerId: number,
    statusId: number,
  ): Promise<Player | undefined> {
    return deleteOne<Player>(`/players/${playerId}/status/${statusId}`);
  },

  savePlayer: async function (player: Player): Promise<Player | undefined> {
    if (!player.id) {
      // if adding a player, pass through everything we know
      return saveOne(player, '/players');
    }
    // ignore values that should not change here. use savePlayerStatus() to change status
    return saveOne(
      { ...player, playerTeams: undefined, status: undefined },
      '/players',
    );
  },

  deletePlayer: async function (
    playerId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/players/${playerId}`);
  },

  mergePlayer: async function (
    intoPlayerId: number,
    fromPlayerId: number,
    options: MergePlayer,
  ): Promise<boolean> {
    return isSuccessful(
      await axios.patch(
        `/players/${intoPlayerId}/mergeFrom/${fromPlayerId}`,
        options,
      ),
    );
  },

  getEvents: async function (
    query: QueryParameters = {},
  ): Promise<EventsOnPage | undefined> {
    return getPage('/events', query);
  },

  saveEvent: async function (event: Event): Promise<Event | undefined> {
    return saveOne(event, '/events');
  },

  deleteEvent: async function (id: number): Promise<ApiResponse | undefined> {
    return deleteOne(`/events/${id}`);
  },

  ignoreEvent: async function (
    id: number | undefined,
    options: IgnoreEventOptions,
  ): Promise<IgnoreEventConfirmationInfo | null> {
    const response: AxiosResponse<IgnoreEventConfirmationInfo> =
      await axios.patch(`/events/${id}/ignore`, options);

    // return null if api call failed, the error somehow appears at the bottom of the screen
    return response.status == 200 ? response.data : null;
  },

  uploadEventFile: async function (
    eventId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFile(`/events/${eventId}/files/${file.name}`, file);
  },

  downloadEventFile: async function (
    eventId: number,
    fileName: string,
  ): Promise<string | undefined> {
    type GetFileResponse = { url: string };
    const response = await axios.get<GetFileResponse>(
      `/events/${eventId}/files/${fileName}`,
    );
    if (!response) return undefined;
    return response.data?.url ?? undefined;
  },

  deleteEventFile: async function (
    eventId: number,
    fileName: string,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/events/${eventId}/files/${fileName}`);
  },

  getAflEvents: async function (
    query: QueryParameters = {},
  ): Promise<EventsOnPage | undefined> {
    return getPage('/aflEvents', query);
  },

  updateExternalConcussion: async function (
    externalConcussion: ExternalConcussion,
  ): Promise<ExternalConcussion | undefined> {
    return saveOne(
      externalConcussion,
      `/players/${externalConcussion.playerId}/externalConcussions`,
    );
  },

  saveExternalConcussion: async function (
    externalConcussion: ExternalConcussion,
  ): Promise<ExternalConcussion | undefined> {
    return saveOne(
      externalConcussion,
      `/players/${externalConcussion.playerId}/externalConcussions`,
    );
  },

  getExternalConcussions: async function (
    playerId: number,
  ): Promise<ExternalConcussionsOnPage | undefined> {
    return getPage<ExternalConcussionsOnPage>(
      `/players/${playerId}/externalConcussions`,
    );
  },

  getAssessments: async function (
    query: QueryParameters = {},
  ): Promise<AssessmentsOnPage | undefined> {
    return getPage<AssessmentsOnPage>('/assessments', query);
  },

  getAssessment: async function (
    assessmentId: number,
  ): Promise<Assessment | undefined> {
    return getOne<Assessment>(assessmentId, '/assessments/{id}');
  },

  saveAssessment: async function (
    assessment: Assessment,
  ): Promise<Assessment | undefined> {
    return saveOne(assessment, '/assessments');
  },

  deleteAssessment: async function (
    assessmentId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/assessments/${assessmentId}`);
  },

  reviewAssessment: async function (
    assessment: Assessment,
  ): Promise<Assessment | undefined> {
    const response = await axios.patch(
      `/assessments/${assessment.id}/review`,
      assessment,
    );
    return response ? assessment : undefined;
  },

  getAssessmentsOverdue: async function (
    query: QueryParameters = {},
  ): Promise<OverdueAssessmentsOnPage | undefined> {
    return getPage<OverdueAssessmentsOnPage>('/assessments/overdue', query);
  },

  getComprehensives: async function (
    query: QueryParameters = {},
  ): Promise<ComprehensiveAssessmentsOnPage | undefined> {
    return getPage('/comprehensives', query);
  },

  getComprehensive: async function (
    assessmentId: number,
  ): Promise<ComprehensiveAssessment | undefined> {
    return getOne(assessmentId, '/comprehensives/{id}');
  },

  saveComprehensive: async function (
    assessment: ComprehensiveAssessment,
  ): Promise<ComprehensiveAssessment | undefined> {
    return saveOne(assessment, '/comprehensives');
  },

  deleteComprehensive: async function (
    comprehensiveId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/comprehensives/${comprehensiveId}`);
  },

  getICCReviews: async function (
    query: QueryParameters = {},
  ): Promise<ICCReviewsOnPage | undefined> {
    return getPage('/iccreviews', query);
  },

  getICCReview: async function (
    reviewId: number,
  ): Promise<ICCReview | undefined> {
    return getOne<ICCReview>(reviewId, '/iccreviews/{id}');
  },

  saveICCReview: async function (
    review: ICCReview,
  ): Promise<ICCReview | undefined> {
    return saveOne(review, '/iccreviews');
  },

  deleteICCReview: async function (
    reviewId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/iccreviews/${reviewId}`);
  },

  declineICCReview: async function (
    reviewId: number,
  ): Promise<ApiResponse | undefined> {
    const response = await axios.patch<ApiResponse>(
      `/iccreviews/${reviewId}/decline`,
    );
    return response?.data;
  },

  getTournaments: async function (
    query: QueryParameters = {},
  ): Promise<TournamentsOnPage | undefined> {
    return getPage<TournamentsOnPage>('/tournaments', query);
  },

  getTournament: async function (
    tournamentId: number,
  ): Promise<Tournament | undefined> {
    return getOne<Tournament>(tournamentId, '/tournaments/{id}');
  },

  saveTournament: async function (
    tournament: Tournament,
  ): Promise<Tournament | undefined> {
    return saveOne(tournament, '/tournaments');
  },

  deleteTournament: async function (
    tournamentId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/tournaments/${tournamentId}`);
  },

  uploadTournamentLogo: async function (
    tournamentId: number,
    file: File,
  ): Promise<string | undefined> {
    return uploadFileAndWait(`/tournaments/${tournamentId}/uploadPhoto`, file);
  },

  addTournamentTeam: async function (
    tournamentId: number,
    teamId: number,
  ): Promise<Tournament | undefined> {
    const response = await axios.patch(
      `/tournaments/${tournamentId}/teams/${teamId}`,
    );
    return response ? (response.data as Tournament) : undefined;
  },

  addTournamentVenue: async function (
    tournamentId: number,
    venueId: number,
  ): Promise<Tournament | undefined> {
    const response = await axios.patch(
      `/tournaments/${tournamentId}/venues/${venueId}`,
    );
    return response ? (response.data as Tournament) : undefined;
  },

  deleteTournamentTeam: async function (
    tournamentId: number,
    teamId: number,
  ): Promise<Tournament | undefined> {
    return deleteOne(`/tournaments/${tournamentId}/teams/${teamId}`);
  },

  deleteTournamentVenue: async function (
    tournamentId: number,
    venueId: number,
  ): Promise<Tournament | undefined> {
    return deleteOne(`/tournaments/${tournamentId}/venues/${venueId}`);
  },

  updateTournamentFixtures: async function (
    tournamentId: number,
    fixtures: Fixture[],
  ): Promise<Tournament | undefined> {
    const response = await axios.patch(
      `/tournaments/${tournamentId}/fixtures`,
      fixtures,
    );
    return response ? (response.data as Tournament) : undefined;
  },

  getVenues: async function (
    query: QueryParameters = {},
  ): Promise<VenuesOnPage | undefined> {
    return getPage<VenuesOnPage>('/venues', query);
  },

  getVenue: async function (venueId: number): Promise<Venue | undefined> {
    return getOne<Venue>(venueId, '/venues/{id}');
  },

  saveVenue: async function (venue: Venue): Promise<Venue | undefined> {
    return saveOne(venue, '/venues');
  },

  deleteVenue: async function (
    venueId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/venues/${venueId}`);
  },

  mergeVenue: async function (
    intoVenueId: number,
    fromVenueId: number,
  ): Promise<boolean> {
    return isSuccessful(
      await axios.patch(`/venues/${intoVenueId}/mergeFrom/${fromVenueId}`),
    );
  },

  getFixtures: async function (
    query: QueryParameters = {},
  ): Promise<FixturesOnPage | undefined> {
    return getPage<FixturesOnPage>('/fixtures', query);
  },

  getFixture: async function (fixtureId: number): Promise<Fixture | undefined> {
    return getOne<Fixture>(fixtureId, '/fixtures/{id}');
  },

  saveFixture: async function (fixture: Fixture): Promise<Fixture | undefined> {
    return saveOne(fixture, '/fixtures');
  },

  deleteFixture: async function (
    fixtureId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/fixtures/${fixtureId}`);
  },

  mergeFixture: async function (
    intoFixtureId: number,
    fromFixtureId: number,
  ): Promise<boolean> {
    return isSuccessful(
      await axios.patch(
        `/fixtures/${intoFixtureId}/mergeFrom/${fromFixtureId}`,
      ),
    );
  },

  reportPlayersWithNoComprehensiveBaseline: async function (
    query: QueryParameters = {},
  ): Promise<PlayersOnPage | undefined> {
    return getPage('/reports/playersWithNoComprehensiveBaseline', query);
  },

  getApiKeys: async function (
    query: QueryParameters = {},
  ): Promise<ApiKeysOnPage | undefined> {
    return getPage('/apikeys', query);
  },

  createApiKey: async function (
    name: string,
    orgId: number,
  ): Promise<ApiKey | undefined> {
    return saveOne({ name, org: { id: orgId } } as ApiKey, '/apikeys');
  },

  deleteApiKey: async function (
    keyId: number,
  ): Promise<ApiResponse | undefined> {
    return deleteOne(`/apikeys/${keyId}`);
  },

  getTeamPlayerReviews: async function (
    query: QueryParameters,
  ): Promise<TeamPlayerReviewsOnPage | undefined> {
    return getPage('/teamPlayerReviews', query);
  },

  getTeamPlayerReview: async function (
    teamId: number,
  ): Promise<TeamPlayerReview | undefined> {
    const response = await axios.get(`/teams/${teamId}/playerReview`);
    return response?.data;
  },

  createTeamPlayerReview: async function (
    teamId: number,
    userIds: Array<number>,
  ): Promise<TeamPlayerReview | undefined> {
    const response = await axios.post(`/teams/${teamId}/playerReview`, {
      teamId,
      userIds: userIds,
    } as TeamPlayerReviewCreate);
    return response?.data;
  },

  saveTeamPlayerReview: async function (
    review: TeamPlayerReview,
  ): Promise<TeamPlayerReview> {
    const response = await axios.patch(
      `/teams/${review.id}/playerReview`,
      review,
    );
    return response?.data ?? { id: review.id };
  },

  addHIAToEvent: async function (
    eventId: number,
    createHIA: PortalCreateHIA
  ): Promise<boolean> {
    const response = await axios.post(
      `/aflEvents/${eventId}/addHIA`,
      createHIA,
    );
    return response.status == 200;
  },

  addSCAT6ToEvent: async function (
    eventId: number,
    createSCAT6: PortalCreateSCAT6
  ): Promise<boolean> {
    const response = await axios.post(
      `/aflEvents/${eventId}/addSCAT`,
      createSCAT6,
    );
    return response.status == 200;
  },

  addClearanceToEvent: async function (
    eventId: number,
    createClearance: PortalCreateClearance
  ): Promise<boolean> {
    const response = await axios.post(
      `/aflEvents/${eventId}/addClearance`,
      createClearance,
    );
    return response.status == 200;
  },
};
